import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import CreateLimitationModal from "@/components/modals/create-limitation-modal";
import { apiClient } from "@/lib/api-client";

interface Limitation {
  id: string;
  title: string;
  description: string;
  resolution: string;
  createdAt: string;
  updatedAt: string;
}

interface LimitationsSectionProps {
  userId: string;
}

export default function LimitationsSection({ userId }: LimitationsSectionProps) {
  const [limitations, setLimitations] = useState<Limitation[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLimitations = async () => {
    try {
      const response = await fetch(`${process?.env?.NEXT_PUBLIC_API_URL}/limitations?userId=${userId}`);
      if (!response.ok) throw new Error('Failed to fetch limitations');
      const data = await response.json();
      setLimitations(data);
    } catch (error) {
      console.error('Error fetching limitations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLimitations();
  }, [userId]);

  const handleCreateLimitation = async (data: { 
    title: string; 
    description: string; 
    resolution: string; 
  }) => {
    try {
      const response = await apiClient.createLimitation(data);
      setLimitations(prev => [response.limitation as any, ...prev]);
    } catch (error) {
      console.error('Error creating limitation:', error);
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading limitations...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Limitations</h3>
        <Button
          onClick={() => setIsModalOpen(true)}
          size="sm"
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Limitation
        </Button>
      </div>

      <CreateLimitationModal
        isOpen={isModalOpen}
        onOpenChange={() => setIsModalOpen(false)}
        onSubmit={handleCreateLimitation}
      />

      <div className="space-y-4">
        {limitations.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">
            No limitations added yet.
          </p>
        ) : (
          limitations?.map((limitation) => (
            <div
              key={limitation.id}
              className="border rounded-lg p-4 space-y-2"
            >
              <h4 className="font-medium">{limitation.title}</h4>
              <p className="text-sm text-muted-foreground">{limitation.description}</p>
              <div className="pt-2">
                <h5 className="text-sm font-medium">Resolution Steps:</h5>
                <p className="text-sm text-muted-foreground">{limitation.resolution}</p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}